import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState } from "@/components/shared";
import { MessageSquare, Send, ShieldAlert, Trash2, Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Messages({ onActivity }) {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const isLandlord = user.role === "landlord";
  const canDelete = isAdmin || user.role === "landlord";
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // {user_a, user_b, key, label} for admin; otherId string otherwise
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [contacts, setContacts] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const bottomRef = useRef(null);

  const loadThreads = () => {
    const url = isAdmin ? "/admin/messages/threads" : "/messages/threads";
    api.get(url).then((r) => setThreads(r.data)).catch(() => {});
  };

  const openNew = () => {
    api.get("/messages/contacts").then((r) => { setContacts(r.data); setShowNew(true); }).catch(() => {});
  };

  const startWith = (c) => {
    setShowNew(false);
    loadConversation({ user_id: c.user_id, name: c.name, avatar: c.avatar });
  };

  const loadConversation = (thread) => {
    setActive(thread);
    const url = isAdmin
      ? `/admin/messages/thread/${thread.user_a}/${thread.user_b}`
      : `/messages/${thread.user_id}`;
    api.get(url).then((r) => { setMsgs(r.data); onActivity?.(); }).catch(() => {});
  };

  const deleteConversation = async () => {
    if (!active || !canDelete) return;
    if (!window.confirm("Delete this entire conversation? This cannot be undone.")) return;
    try {
      if (isAdmin) {
        await api.delete(`/admin/messages/thread/${active.user_a}/${active.user_b}`);
      } else {
        await api.delete(`/messages/${active.user_id}`);
      }
      toast.success("Conversation deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete conversation");
    }
    setActive(null);
    setMsgs([]);
    loadThreads();
    onActivity?.();
  };

  const activeLabel = !active
    ? ""
    : isAdmin
      ? `${active.user_a_info?.name} ↔ ${active.user_b_info?.name}`
      : active.name;

  useEffect(() => {
    loadThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const send = async () => {
    if (!text.trim() || !active) return;
    try {
      if (isAdmin) {
        await api.post("/admin/messages/reply", { user_a: active.user_a, user_b: active.user_b, body: text });
      } else {
        await api.post("/messages", { recipient_id: active.user_id, body: text });
      }
    } catch (e) { console.error("Failed to send message", e); }
    setText("");
    loadConversation(active);
    loadThreads();
  };

  return (
    <div className="bg-white border border-line rounded-xl overflow-hidden grid md:grid-cols-3 h-[560px]">
      <div className="border-r border-line overflow-y-auto">
        <div className="p-4 border-b border-line font-heading font-medium text-ink flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            {isAdmin && <ShieldAlert className="h-4 w-4 text-forest" />}
            {isAdmin ? "All conversations" : "Conversations"}
          </span>
          {isLandlord && !showNew && (
            <button data-testid="new-message-btn" onClick={openNew} className="inline-flex items-center gap-1 text-xs font-medium text-forest border border-forest/30 rounded-lg px-2.5 py-1 hover:bg-forest/5 transition-colors">
              <Plus className="h-3.5 w-3.5" /> New
            </button>
          )}
        </div>
        {showNew ? (
          <div data-testid="new-message-panel">
            <button data-testid="new-message-back" onClick={() => setShowNew(false)} className="w-full text-left px-4 py-2.5 text-xs text-mute hover:bg-bone flex items-center gap-1.5 border-b border-line">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to conversations
            </button>
            {contacts.length === 0 ? (
              <p className="p-4 text-sm text-mute">No renters or applicants yet. They'll appear here once someone applies to or leases one of your properties.</p>
            ) : (
              contacts.map((c) => (
                <button
                  key={c.user_id}
                  data-testid={`contact-${c.user_id}`}
                  onClick={() => startWith(c)}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-bone transition-colors"
                >
                  {c.avatar ? <img src={c.avatar} alt="" className="h-9 w-9 rounded-full" /> : <div className="h-9 w-9 rounded-full bg-forest/10 text-forest flex items-center justify-center text-sm font-semibold">{(c.name || "?")[0]}</div>}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink truncate">{c.name}</p>
                    <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${c.context === "Tenant" ? "text-forest bg-forest/10" : "text-stone-600 bg-stone-100"}`}>{c.context}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        ) : threads.length === 0 ? (
          <p className="p-4 text-sm text-mute">No conversations yet.</p>
        ) : isAdmin ? (
          threads.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-thread-${t.key}`}
              onClick={() => loadConversation(t)}
              className={`w-full text-left px-4 py-3 hover:bg-bone transition-colors ${active?.key === t.key ? "bg-bone" : ""}`}
            >
              <p className="text-sm font-medium text-ink truncate">
                {t.user_a_info?.name} ↔ {t.user_b_info?.name}
              </p>
              <p className="text-xs text-mute truncate mt-0.5">{t.last_message}</p>
              <p className="text-[11px] text-mute mt-0.5 capitalize">
                {t.user_a_info?.role} · {t.user_b_info?.role} · {t.count} message{t.count === 1 ? "" : "s"}
              </p>
            </button>
          ))
        ) : (
          threads.map((t) => (
            <button
              key={t.user_id}
              data-testid={`thread-${t.user_id}`}
              onClick={() => loadConversation(t)}
              className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-bone transition-colors ${active?.user_id === t.user_id ? "bg-bone" : ""}`}
            >
              <img src={t.avatar} alt="" className="h-9 w-9 rounded-full" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink truncate">{t.name}</p>
                <p className="text-xs text-mute truncate">{t.last_message}</p>
              </div>
            </button>
          ))
        )}
      </div>

      <div className="md:col-span-2 flex flex-col">
        {!active ? (
          <EmptyState icon={MessageSquare} title="Select a conversation" subtitle={isAdmin ? "Oversee any thread and reply as Support." : "Your messages with landlords and renters appear here."} />
        ) : (
          <>
            <div className="flex items-center justify-between px-5 py-3 border-b border-line">
              <p className="text-sm font-medium text-ink truncate">{activeLabel}</p>
              {canDelete && (
                <button
                  data-testid="delete-conversation-btn"
                  onClick={deleteConversation}
                  className="inline-flex items-center gap-1.5 text-xs text-red-600 border border-red-200 rounded-lg px-2.5 py-1.5 hover:bg-red-50 transition-colors shrink-0"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-bone/40">
              {msgs.map((m) => {
                const mine = m.sender_id === user.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[75%]">
                      {isAdmin && !mine && <p className="text-[11px] text-mute mb-0.5 ml-1">{m.sender_name}</p>}
                      <div className={`px-4 py-2 rounded-2xl text-sm ${mine ? "bg-forest text-white rounded-br-sm" : "bg-white border border-line text-ink rounded-bl-sm"}`}>
                        {m.body}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            <div className="p-3 border-t border-line flex gap-2">
              <input
                data-testid="message-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={isAdmin ? "Reply as Support…" : "Type a message…"}
                className="flex-1 bg-bone border border-line rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20"
              />
              <button data-testid="message-send" onClick={send} className="bg-forest text-white px-4 rounded-lg hover:bg-forest-hover transition-colors">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
